/**
 * 절차 생성 BGM.
 *
 * 맵 무드(field / forest)에 따라 펜타토닉 스케일에서 아르페지오와 베이스를
 * 스텝 시퀀서로 찍어 반복한다. 음원 파일은 쓰지 않는다.
 */

import { synth } from './Synth';

type Mood = 'field' | 'forest';

interface MoodConfig {
  /** 한 스텝 길이 (초) */
  step: number;
  /** 아르페지오 음 (Hz) */
  lead: readonly number[];
  /** 베이스 음 (Hz) */
  bass: readonly number[];
  leadType: OscillatorType;
  bassType: OscillatorType;
  leadGain: number;
  bassGain: number;
  /** 16스텝 패턴 중 리드가 울리는 위치 */
  leadPattern: readonly number[];
  bassPattern: readonly number[];
}

const MOODS: Record<Mood, MoodConfig> = {
  // 밝은 메이저 펜타토닉 — 초원 느낌
  field: {
    step: 0.2,
    lead: [523.25, 587.33, 659.25, 783.99, 880, 1046.5],
    bass: [130.81, 174.61, 196, 174.61],
    leadType: 'triangle',
    bassType: 'sine',
    leadGain: 0.09,
    bassGain: 0.12,
    leadPattern: [0, 2, 4, 6, 7, 8, 10, 12, 14],
    bassPattern: [0, 4, 8, 12],
  },
  // 어두운 마이너 펜타토닉 — 숲 느낌
  forest: {
    step: 0.24,
    lead: [440, 523.25, 587.33, 659.25, 783.99, 880],
    bass: [110, 146.83, 130.81, 98],
    leadType: 'sine',
    bassType: 'triangle',
    leadGain: 0.08,
    bassGain: 0.13,
    leadPattern: [0, 3, 6, 8, 11, 14],
    bassPattern: [0, 6, 8, 14],
  },
};

const STEPS = 16;

export class Bgm {
  private timer: number | null = null;
  private mood: Mood | null = null;
  private step = 0;
  private bar = 0;

  play(mood: Mood): void {
    if (this.mood === mood && this.timer !== null) return;
    this.stop();
    synth.start();
    if (!synth.ready) return;

    this.mood = mood;
    this.step = 0;
    this.bar = 0;
    const cfg = MOODS[mood];
    this.timer = window.setInterval(() => this.tick(cfg), cfg.step * 1000);
    this.tick(cfg);
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.mood = null;
  }

  private tick(cfg: MoodConfig): void {
    const bus = synth.bgmOut;
    if (!bus) return;

    const s = this.step;

    if (cfg.leadPattern.includes(s)) {
      const idx = (s + this.bar * 3) % cfg.lead.length;
      const octave = this.bar % 4 === 3 ? 2 : 1;
      synth.tone(
        {
          freq: cfg.lead[idx] * (octave === 2 ? 1.5 : 1),
          type: cfg.leadType,
          duration: cfg.step * 1.7,
          gain: cfg.leadGain,
          attack: 0.02,
        },
        bus,
      );
    }

    if (cfg.bassPattern.includes(s)) {
      const idx = Math.floor(s / 4) % cfg.bass.length;
      synth.tone(
        {
          freq: cfg.bass[idx],
          type: cfg.bassType,
          duration: cfg.step * 3.2,
          gain: cfg.bassGain,
          attack: 0.03,
        },
        bus,
      );
    }

    // 4마디마다 살짝 반짝이는 장식음
    if (s === 15 && this.bar % 2 === 1) {
      synth.sequence(
        [cfg.lead[cfg.lead.length - 1], cfg.lead[cfg.lead.length - 1] * 1.5],
        cfg.step * 0.5,
        { type: 'sine', gain: cfg.leadGain * 0.8, noteDuration: cfg.step },
        bus,
      );
    }

    this.step = (s + 1) % STEPS;
    if (this.step === 0) this.bar++;
  }
}

export const bgm = new Bgm();
